import { detach, getParentOfType, } from 'mobx-state-tree';
import { v4 } from 'uuid';
import schema, { generateType, updateFieldsGenerator } from './Schema';
import { moveItem } from './CourseModel';
export const LearningBoardSection = generateType(schema.getModel('learningBoardSection'))
    .actions((self) => ({
    addChange() {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        getParentOfType(self, LearningBoard).addChange();
    },
    addItem(name) {
        self.sections.push(
        // newSection
        {
            id: v4(),
            type: 'custom',
            name,
            sections: [],
        });
        this.addChange();
    },
    updateFields: updateFieldsGenerator(self),
}))
    .views((self) => ({
    canHaveSubSections: () => (self.type === 'custom'),
    canDelete: () => (true),
}));
export const LearningBoard = generateType(schema.getModel('learningBoard'))
    .actions((self) => ({
    addCategory(name) {
        const newSection = LearningBoardSection.create({
            id: v4(),
            type: 'category',
            name,
        });
        self.sections.push(newSection);
        self.addChange();
        return newSection;
    },
    addCustom(name) {
        const newSection = LearningBoardSection.create({
            id: v4(),
            type: 'custom',
            name,
            sections: [],
        });
        self.sections.push(newSection);
        self.addChange();
        return newSection;
    },
    moveSection(section, newIndex) {
        const currentIndex = self.sections.indexOf(section);
        const adjustedNewIndex = newIndex > currentIndex ? newIndex - 1 : newIndex;
        const tempArray = self.sections.slice();
        moveItem(tempArray, currentIndex, adjustedNewIndex);
        self.sections.replace(tempArray);
        self.addChange();
    },
    /**
     * Returns the subSection from the section and returns it.
     * Section is matched by ID.
     * @param subSection
     */
    removeSection(toRemove) {
        detach(toRemove);
        self.addChange();
    },
    /**
     * Adds a subsection into a section at the specified index.  If index is negative
     * it is added at the end.
     * @param subSection
     * @param atIndex
     */
    insertSection(subSection, atIndex) {
        if (atIndex < 0) {
            self.sections.push(subSection);
        }
        else {
            self.sections.splice(atIndex, 0, subSection);
        }
        self.addChange();
    },
    /**
     *
     * @param fields JSON, it is a flat list of key value pairs
     */
    updateFields: updateFieldsGenerator(self),
}))
    .views(() => ({
    canDelete: () => (false),
}));
export const Customer = generateType(schema.getModel('customer'))
    .views((self) => ({
    getMembershipCount() {
        return self.members?.items ? self.members.items.length : 0;
    },
    getMembershipStartDate() {
        if (self.members?.items?.length) {
            const findIndex = self.members.items.findIndex((m) => m.status === 'ACTIVE');
            if (findIndex > -1) {
                return self.members.items[findIndex].createdAt;
            }
        }
        return '-';
    },
    getMembershipStatus() {
        if (self.members?.items?.length) {
            return self.members.items[0].status;
        }
        return '-';
    },
}))
    .actions((self) => ({
    isAdmin() {
        return (self.id.startsWith('admin_'));
    },
    membershipCounts() {
        return {
            active: self.activeMembershipCount,
            trial: self.trialMembershipCount,
            overdue: self.overdueMembershipCount,
            expired: self.expiredMembershipCount,
        };
    },
}));
export const Payment = generateType(schema.getModel('payment'))
    .actions((self) => ({
    // This is an auto-generated example action.
    log() {
        console.log(JSON.stringify(self));
    },
}));
export const customerInfoGQL = /* GraphQL */ (portalApi) => (`
  query CustomerInfo(
    ${portalApi ? '' : '$organizationID: String'} 
    $portalID: String
  ) {
    customerInfo(
      portalID: $portalID
      ${portalApi ? '' : 'organizationID: $organizationID'}
    ) {
      Cognito {
        Username
        UserCreateDate
        UserLastModifiedDate
        Enabled
        UserStatus
        PreferredMfaSetting
        UserMFASettingList
      }
      Access {
        groups
        purchasedCourses
        purchasedPrograms
      }
      UserAttributes {
        Name
        Value
      }
      Internal {
        id
        ${portalApi ? '' : 'organizationID'}
        createdAt
        updatedAt
        name
        phoneNumber
        hubID
        biography
        profileImage
        email
        background
        headline
        title
        text1 text2 text3 text4 text5
      }
      Members {
        ${schema.getGqlPropertyList('member', portalApi).join(' ')}
        # id
        # membershipID
        # name
        # status
        # lastPaymentAt
        # nextPaymentAt
        # failedPaymentAttempts
      }
      CourseProgresses {
        id  courseID 
        ${portalApi ? '' : 'customerID'}
        organizationID
        createdAt updatedAt progress startedDate completeDate
        lastActiveDate expiryDate lessonProgress lessonsCompleted
        quizResults notes
        hasCertification certificates certifications
        certificateAwardedAt certificateScore certificateStatus
        completedCertifications scormData
      }
      ProgramProgresses {
        id programID 
        ${portalApi ? '' : 'customerID'}
        organizationID
        createdAt updatedAt progress startedDate completeDate
        lastActiveDate expiryDate sectionProgress lessonsCompleted
        quizResults notes courseItems
        hasCertification certificates certifications
        certificateAwardedAt certificateScore certificateStatus
        completedCertifications
      }
      MicrocastProgresses {
        id microcastID 
        ${portalApi ? '' : 'customerID'}
        organizationID
        createdAt updatedAt progress startedDate completeDate
        lastActiveDate expiryDate
        notes scormData
      }
      DocumentProgresses {
        id documentKey
        ${portalApi ? '' : 'customerID'}
        organizationID
        createdAt updatedAt progress startedDate completeDate
        progress viewCount history includeInMyDocuments lastActiveDate expiryDate
        notes
      }
      MeetingRegistrations {
        id meetingID 
        ${portalApi ? '' : 'customerID'}
        organizationID
        createdAt updatedAt status
        notes name history portalID
      }
    }
  }
`);
export const getLearningBoardGQL = (portalApi) => (
/* GraphQL */ `
query learningBoard(
  ${portalApi ? '' : '$organizationID: String'} 
  $portalID: String
) {
  learningBoard(
    portalID: $portalID
    ${portalApi ? '' : 'organizationID: $organizationID'}
  ) {
      id
      organizationID
      portalID
      customerID
      createdAt
      updatedAt
      name
      status
      sections
  }
}
`);
export const listQuestionairesByEmailGQL = (portalApi) => (`
  query ListQuestionairesByEmail(
    ${portalApi ? '' : '$organizationID: ID'} 
    $portalIDEmailStatus: ${portalApi ? '' : 'Model'}QuestionnaireByOrganizationEmailCompositeKeyConditionInput
    $sortDirection: ${portalApi ? '' : 'Model'}SortDirection
    $filter: ${portalApi ? '' : 'Model'}QuestionnaireFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listQuestionairesByEmail(
      ${portalApi ? '' : 'organizationID: $organizationID'}
      portalIDEmailStatus: $portalIDEmailStatus
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        ${schema.getGqlPropertyList('questionnaire', portalApi).join(' ')}
      }
      nextToken
      __typename
    }
  }
`);
export const refreshCustomerStatsGql = /* GraphQL */ `
query refreshCustomerStats(
  $organizationID: String
  $customerID: String
) {
  refreshCustomerStats(
    organizationID: $organizationID
    customerID: $customerID
  ) {
      ${schema.getGqlPropertyList('customer', false, true)}
  }
}
`;
export const setCustomerAttributesGQL = (portalApi) => (`
mutation setCustomerAttributes(
  ${portalApi ? '' : '$organizationID: ID'} 
  $customerID: String
  $attributes: [ValueInput]
) {
  setCustomerAttributes(
    ${portalApi ? '' : 'organizationID: $organizationID'}
    customerID: $customerID
    attributes: $attributes
  )
}
`);
export const invokeAgentGQL = (portalApi) => (`
query InvokeAgent(
  ${portalApi ? '' : '$organizationID: ID!'} 
  $portalID: ID!,
  $prompt: String!
  $sessionID: String
  $options: AWSJSON
  $examples: AWSJSON
) {
  invokeAgent(
    ${portalApi ? '' : 'organizationID: $organizationID'}
    portalID: $portalID
    sessionID: $sessionID
    prompt: $prompt
    options: $options
    ${portalApi ? '' : 'adminOptions: $adminOptions'}
  )
}
`);
export const getAgentSessionGQL = (portalApi) => (`
  query GetAgentSession(
    ${portalApi ? '' : '$organizationID: ID!'} 
    $portalID: ID!,
    $sessionID: String
  ) {
    getAgentSession(
      ${portalApi ? '' : 'organizationID: $organizationID'}
      portalID: $portalID
      sessionID: $sessionID
    )
  }
`);
//# sourceMappingURL=CustomerModel.js.map